'use client'

import React, { useEffect, useRef } from 'react'

import { CategoryFilterNav } from '@/components/CategoryFilterNav'
import { containerClassName } from '@/components/Container'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { scrollExpandedAccordionItemOnMobile } from '@/components/FilterAccordionSection/mobileAccordionScroll'
import { cn } from '@/utilities/ui'

export type FilterAccordionIllustration = {
  className?: string
  height: number
  src: string
  width: number
}

type FilterAccordionSectionBaseProps = {
  ariaLabelledBy: string
  gridColumnsClassName?: string
  gridSpacing?: 'default' | 'compact'
  mobileIllustration?: FilterAccordionIllustration
  sidebar: React.ReactNode
}

type FilterAccordionSectionWithChildrenProps = FilterAccordionSectionBaseProps & {
  children: React.ReactNode
}

type FilterAccordionSectionItemsProps<TItem> = FilterAccordionSectionBaseProps & {
  accordionItemGap?: 'default' | 'tight'
  activeCategory: string
  afterAccordion?: React.ReactNode
  beforeFilters?: React.ReactNode
  categoryFiltersAriaLabel: string
  categoryLabels: Record<string, string>
  categoryOrder: readonly string[]
  children?: never
  expandedValue: string | undefined
  filterLayoutId: string
  getItemId: (item: TItem) => string
  getItemKey: (item: TItem) => string
  getItemValue: (item: TItem) => string
  items: TItem[]
  onAccordionChange: (value: string | undefined) => void
  onCategoryChange: (category: string) => void
  renderContent: (item: TItem) => React.ReactNode
  renderTrigger: (item: TItem) => React.ReactNode
  scrollExpandedItemOnMobile?: boolean
}

export type FilterAccordionSectionProps<TItem = unknown> =
  | FilterAccordionSectionWithChildrenProps
  | FilterAccordionSectionItemsProps<TItem>

export function FilterAccordionSection<TItem = unknown>(
  props: FilterAccordionSectionProps<TItem>,
) {
  const {
    ariaLabelledBy,
    gridColumnsClassName = 'lg:grid-cols-[minmax(0,26rem)_minmax(0,1fr)]',
    gridSpacing = 'default',
    mobileIllustration,
    sidebar,
  } = props

  const scrollAbortRef = useRef<AbortController | null>(null)

  const handleCategoryChange = (category: string) => {
    scrollAbortRef.current?.abort()
    scrollAbortRef.current = null
    if ('onCategoryChange' in props && props.onCategoryChange) {
      props.onCategoryChange(category)
    }
  }

  const handleAccordionChange = (value: string) => {
    if (!('onAccordionChange' in props) || !props.onAccordionChange) return

    const closingId = 'expandedValue' in props ? props.expandedValue : undefined

    scrollAbortRef.current?.abort()
    scrollAbortRef.current = null

    props.onAccordionChange(value || undefined)

    if (!('scrollExpandedItemOnMobile' in props) || !props.scrollExpandedItemOnMobile || !value) return

    const controller = new AbortController()
    scrollAbortRef.current = controller

    void scrollExpandedAccordionItemOnMobile({
      closeItemId: closingId,
      openItemId: value,
      signal: controller.signal,
    }).catch(() => {
      // Ignore aborted scroll requests from rapid accordion changes.
    })
  }

  useEffect(() => {
    return () => {
      scrollAbortRef.current?.abort()
    }
  }, [])

  return (
    <section aria-labelledby={ariaLabelledBy} className="bg-cream">
      <div className={cn(containerClassName, 'py-12 lg:py-20')}>
        <div
          className={cn(
            'grid gap-y-4 lg:gap-y-0',
            gridColumnsClassName,
            gridSpacing === 'compact' ? 'gap-6 lg:gap-x-10' : 'gap-10 lg:gap-x-16',
          )}
        >
          <div className="flex flex-col lg:sticky lg:top-24 lg:self-start">{sidebar}</div>

          <div className="min-w-0">
            {'children' in props && props.children ? (
              props.children
            ) : 'items' in props ? (
              <>
                {props.beforeFilters}

                <CategoryFilterNav
                  activeCategory={props.activeCategory}
                  ariaLabel={props.categoryFiltersAriaLabel}
                  categoryLabels={props.categoryLabels}
                  categoryOrder={[...props.categoryOrder]}
                  className="mb-6 lg:mb-8"
                  desktopAlign="start"
                  desktopWidth="fit"
                  layoutId={props.filterLayoutId}
                  onSelect={handleCategoryChange}
                />

                <Accordion
                  className={props.accordionItemGap === 'tight' ? 'gap-2' : 'gap-4'}
                  collapsible
                  onValueChange={handleAccordionChange}
                  type="single"
                  value={props.expandedValue}
                  variant="faq"
                >
                  {props.items.map((item) => (
                    <AccordionItem
                      key={props.getItemKey(item)}
                      id={props.getItemId(item)}
                      value={props.getItemValue(item)}
                    >
                      <AccordionTrigger>{props.renderTrigger(item)}</AccordionTrigger>
                      <AccordionContent>{props.renderContent(item)}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>

                {props.afterAccordion}
              </>
            ) : null}
          </div>

          {mobileIllustration ? (
            <img
              alt=""
              aria-hidden
              className={
                mobileIllustration.className ??
                'mx-auto h-auto w-full max-w-[12rem] lg:hidden'
              }
              height={mobileIllustration.height}
              loading="lazy"
              src={mobileIllustration.src}
              width={mobileIllustration.width}
            />
          ) : null}
        </div>
      </div>
    </section>
  )
}
